import { RegionFaq } from "@/containers/RegionParlorsContainer/components/RegionFAQ";

import { toManwon } from "./formatMoney";
import { RegionFacilitiesData } from "./regionFacilities";

/**
 * 지역 페이지 FAQ. 모든 답변은 이 지역의 실제 시설 데이터에서 만든다.
 * 데이터가 없는 항목은 문항 자체를 넣지 않는다(빈 값으로 문장이 깨지는 것보다 낫다).
 */
export function getRegionFaqs(data: RegionFacilitiesData): RegionFaq[] {
  const {
    sigunguName,
    hasParlorInRegion,
    parlors,
    publicCount,
    insideCrematorium,
    crematoria,
    familyCostMin,
    familyCostMax,
    burials,
  } = data;

  const faqs: RegionFaq[] = [];

  // 장례식장이 0곳인 시군구는 parlors 가 인근 시설이다. 시군구명으로 라벨하면
  // "양양군 장례식장 중 가장 낮은 곳" 이 다른 시군구 시설을 가리키는 거짓이 된다.
  // 화장장 문항은 관내, 관외 판정이 주민 기준이라 시군구명을 그대로 쓴다
  const parlorLabel = hasParlorInRegion ? sigunguName : `${sigunguName} 인근`;

  const cheapest = parlors
    .filter((parlor) => parlor.binsoMinCharge !== null)
    .sort(
      (a, b) => (a.binsoMinCharge as number) - (b.binsoMinCharge as number)
    )[0];

  if (cheapest) {
    faqs.push({
      question: `${parlorLabel} 장례식장 중 빈소 사용료가 가장 낮은 곳은 어디입니까?`,
      answer:
        `공개된 요금표 기준으로는 ${cheapest.companyName}이 ${toManwon(
          cheapest.binsoMinCharge
        )}으로 가장 낮습니다. ` +
        `다만 시설마다 빈소 면적과 요금 기준이 달라 사용료만으로 단순 비교하기는 어렵습니다. ` +
        `최종 비용은 빈소 규모, 조문객 수에 따른 접객 음식, 장례용품에 따라 결정됩니다.`,
    });
  }

  if (publicCount > 0) {
    faqs.push({
      question: "공설 장례식장과 사설 장례식장은 무엇이 다릅니까?",
      answer:
        `공설은 지자체가 운영하며 조례로 요금이 정해져 있어 사설보다 저렴한 편입니다. ` +
        `대신 빈소 수가 적어 예약이 어렵고, 일부 시설은 관내 거주 요건이 있습니다. ` +
        `${parlorLabel}에는 공설 ${publicCount}곳이 있습니다. ` +
        `사설은 접객 시설과 주차 규모가 크고 예약이 비교적 수월합니다.`,
    });
  }

  if (insideCrematorium) {
    const outsideExamples = crematoria
      .filter((crematorium) => crematorium.tier !== "inside")
      .slice(0, 2)
      .map((crematorium) => crematorium.companyName);

    faqs.push({
      question: `${sigunguName} 주민은 화장장 요금을 얼마나 냅니까?`,
      answer:
        `${insideCrematorium.companyName}을 이용하면 관내 요금 ${insideCrematorium.appliedPrice}이 적용됩니다. ` +
        (outsideExamples.length
          ? `${outsideExamples.join(", ")} 등 인근 시설은 관외 요금 ${
              insideCrematorium.outsidePrice
            } 수준이 적용되므로, `
          : `관외 시설은 ${insideCrematorium.outsidePrice} 수준이므로, `) +
        `관내 시설의 예약 가능 여부를 먼저 확인하시는 편이 좋습니다.`,
    });
  } else if (crematoria.length) {
    faqs.push({
      question: `${sigunguName}에서 화장장을 이용하려면 어디로 가야 합니까?`,
      answer:
        `${sigunguName} 관내에는 관내 요금이 적용되는 화장장이 없어, 인근 지자체 시설을 관외 요금으로 이용하게 됩니다. ` +
        `가장 가까운 시설은 ${crematoria[0].companyName}이며 적용 요금은 ${crematoria[0].appliedPrice}입니다. ` +
        `관외 요금은 지자체 조례에 따라 다르므로 예약 전 확인이 필요합니다.`,
    });
  }

  // burials 는 요금 공개 시설이 앞에 오도록 정렬돼 있지만, 요금을 공개한 장지가
  // 한 곳도 없는 지역(가격 비공개 계약 시설만 있는 경우 포함)이면 null 이 된다.
  // 그때는 "null부터 시작합니다" 가 찍히므로 문장을 아예 뺀다
  const burialMinLabel = toManwon(burials[0]?.minCharge);

  if (familyCostMin !== null && familyCostMax !== null) {
    faqs.push({
      question: `${sigunguName}에서 가족장 비용은 얼마나 잡아야 합니까?`,
      answer:
        `${parlorLabel} 장례식장의 가족장 예상 시설 비용은 ${toManwon(
          familyCostMin
        )}에서 ${toManwon(familyCostMax)} 사이입니다. ` +
        `여기에 화장 요금과 장지 비용이 별도로 더해집니다. ` +
        (burialMinLabel
          ? `장지는 선택한 유형에 따라 ${burialMinLabel}부터 시작합니다. `
          : "") +
        `장례지도사 서비스와 장례용품 비용은 구성에 따라 달라지므로 상담에서 항목별로 확인하실 수 있습니다.`,
    });
  }

  return faqs;
}
